import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { PartnerDashboardAPIResponse } from "./Types";

type PartnerDashboardState = {
  data: PartnerDashboardAPIResponse | null;
  partnerId: number | null;
  status: {
    loading: boolean;
    error: string | null;
    supportStatusSaving: boolean;
    supportStatusError: string | null;
    contractReminderLoading: boolean;
    contractReminderStatus: "idle" | "success" | "error";
    contractReminderMessage: string | null;
  };
};

const initialState: PartnerDashboardState = {
  data: null,
  partnerId: null,
  status: {
    loading: false,
    error: null,
    supportStatusSaving: false,
    supportStatusError: null,
    contractReminderLoading: false,
    contractReminderStatus: "idle",
    contractReminderMessage: null,
  },
};

const partnerDashboardSlice = createSlice({
  name: "partner_dashboard",
  initialState,
  reducers: {
    setPartnerDashboardLoading: (state, action: PayloadAction<boolean>) => {
      state.status.loading = action.payload;
    },
    setPartnerDashboardError: (state, action: PayloadAction<string | null>) => {
      state.status.error = action.payload;
    },
    setPartnerDashboardData: (
      state,
      action: PayloadAction<{
        partnerId: number;
        data: PartnerDashboardAPIResponse;
      }>
    ) => {
      state.data = action.payload.data;
      state.partnerId = action.payload.partnerId;
      state.status.error = null;
    },
    clearPartnerDashboard: (state) => {
      state.data = null;
      state.partnerId = null;
      state.status.loading = false;
      state.status.error = null;
      state.status.supportStatusSaving = false;
      state.status.supportStatusError = null;
      state.status.contractReminderLoading = false;
      state.status.contractReminderStatus = "idle";
      state.status.contractReminderMessage = null;
    },
    setSupportStatusSaving: (state, action: PayloadAction<boolean>) => {
      state.status.supportStatusSaving = action.payload;
    },
    setSupportStatusError: (state, action: PayloadAction<string | null>) => {
      state.status.supportStatusError = action.payload;
    },
    updatePartnerSupportStatus: (
      state,
      action: PayloadAction<{
        support_level: string;
        support_status: number;
      }>
    ) => {
      if (state.data?.summary) {
        state.data.summary.support_level = action.payload.support_level;
        state.data.summary.support_status = action.payload.support_status;
      }
      state.status.supportStatusError = null;
    },
    setContractReminderLoading: (state, action: PayloadAction<boolean>) => {
      state.status.contractReminderLoading = action.payload;
    },
    setContractReminderResult: (
      state,
      action: PayloadAction<{
        status: "success" | "error";
        message: string;
      }>
    ) => {
      state.status.contractReminderStatus = action.payload.status;
      state.status.contractReminderMessage = action.payload.message;
    },
    resetContractReminderStatus: (state) => {
      state.status.contractReminderStatus = "idle";
      state.status.contractReminderMessage = null;
    },
  },
});

export const partnerDashboardSliceReducer = partnerDashboardSlice.reducer;
export const partnerDashboardSliceAction = partnerDashboardSlice.actions;
